"use client";

import type {
  ChannelFeedEngagement,
  ChannelFeedReaction,
} from "@mlx/contracts";
import { SmilePlusIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { REACTION_EMOJI } from "./feed-engagement";

const REACTIONS = Object.keys(REACTION_EMOJI) as ChannelFeedReaction[];
const REACTION_LABEL: Record<ChannelFeedReaction, string> = {
  thumbs_up: "Thumbs up",
  heart: "Love",
  laugh: "Laugh",
  celebrate: "Celebrate",
  eyes: "Seen",
  check: "Handled",
};

/** Existing reactions as pills, with the add button after them. */
export function ChannelThreadReactionPicker({
  engagement,
  disabled,
  label = "Add reaction",
  className,
  showAddButton = true,
  onToggle,
}: {
  engagement: Pick<ChannelFeedEngagement, "reactions"> | undefined;
  disabled: boolean;
  label?: string;
  className?: string;
  /** Hosts that place the add button elsewhere (a comment's trailing
   *  column) pass false and render only the pills here. */
  showAddButton?: boolean;
  onToggle: (reaction: ChannelFeedReaction, active: boolean) => Promise<void>;
}) {
  const activeReactions =
    engagement?.reactions.filter((reaction) => reaction.count > 0) ?? [];
  return (
    <div className={cn("mt-1 flex flex-wrap items-center gap-1", className)}>
      {activeReactions.map((current) => (
        <button
          key={current.reaction}
          type="button"
          disabled={disabled}
          aria-pressed={current.reactedByViewer}
          aria-label={`${REACTION_LABEL[current.reaction]} reaction, ${current.count}`}
          className="inline-flex h-6 items-center gap-1 rounded-full border border-border/70 bg-background px-1.5 text-2xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground aria-pressed:border-primary/30 aria-pressed:bg-primary/10 aria-pressed:text-primary"
          onClick={() =>
            void onToggle(current.reaction, !current.reactedByViewer)
          }
        >
          <span aria-hidden>{REACTION_EMOJI[current.reaction]}</span>
          <span>{current.count}</span>
        </button>
      ))}
      {showAddButton ? (
        <ChannelThreadReactionMenuButton
          engagement={engagement}
          disabled={disabled}
          label={label}
          onToggle={onToggle}
        />
      ) : null}
    </div>
  );
}

/** The add-reaction button with its menu; the menu opens below the button
 *  and aligns to whichever edge the host sits against. */
export function ChannelThreadReactionMenuButton({
  engagement,
  disabled,
  label = "Add reaction",
  align = "start",
  className,
  onToggle,
}: {
  engagement: Pick<ChannelFeedEngagement, "reactions"> | undefined;
  disabled: boolean;
  label?: string;
  align?: "start" | "end";
  className?: string;
  onToggle: (reaction: ChannelFeedReaction, active: boolean) => Promise<void>;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pickerOpen) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!pickerRef.current?.contains(event.target as Node))
        setPickerOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPickerOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [pickerOpen]);
  return (
    <div ref={pickerRef} className={cn("relative", className)}>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        disabled={disabled}
        aria-label={label}
        title={label}
        aria-expanded={pickerOpen}
        aria-haspopup="menu"
        className="size-6 rounded-full text-muted-foreground"
        onClick={() => setPickerOpen((open) => !open)}
      >
        <SmilePlusIcon />
      </Button>
      {pickerOpen ? (
        <div
          role="menu"
          aria-label={label}
          className={cn(
            "absolute top-full z-(--z-overlay) mt-1 min-w-40 rounded-lg bg-popover/95 p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 backdrop-blur-xl",
            align === "end" ? "right-0" : "left-0",
          )}
        >
          {REACTIONS.map((reaction) => {
            const current = engagement?.reactions.find(
              (item) => item.reaction === reaction,
            );
            return (
              <button
                key={reaction}
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-sm font-medium outline-none hover:bg-foreground/10 focus-visible:bg-foreground/10"
                onClick={() => {
                  setPickerOpen(false);
                  void onToggle(reaction, !(current?.reactedByViewer ?? false));
                }}
              >
                <span className="w-5 text-center" aria-hidden>
                  {REACTION_EMOJI[reaction]}
                </span>
                {current?.reactedByViewer
                  ? `Remove ${REACTION_LABEL[reaction]}`
                  : REACTION_LABEL[reaction]}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
